import React, { useEffect, useState } from "react";
import "animate.css";
import useScrollAnimation from "../useScrollAnimation";
import { Link } from "react-router-dom";
import {
  listSlumSoccerProjects,
  listSlumSoccerEvents,
} from "../graphql/queries";
import { generateClient } from "aws-amplify/api";
import { ProjectCard } from "../components/ProjectCard";
import { EventCard } from "../components/EventCard";

const client = generateClient();

export function HomePage(props) {
  useScrollAnimation();
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProjectsAndEvents();
  }, []);

  const parseDate = (ef1) => {
    try {
      if (typeof ef1 === "object" && ef1.date) {
        return new Date(ef1.date);
      }
      if (typeof ef1 === "string") {
        const parsedEf1 = JSON.parse(ef1);
        return new Date(parsedEf1.date);
      }
      return new Date(0);
    } catch (error) {
      console.error("Error parsing date:", error);
      return new Date(0);
    }
  };

  const fetchProjectsAndEvents = async () => {
    try {
      const projectResponse = await client.graphql({
        query: listSlumSoccerProjects,
        variables: { limit: 10 },
      });

      const sortedProjects = projectResponse.data.listSlumSoccerProjects.items
        .filter((project) => project.ef1)
        .sort((a, b) => parseDate(b.ef1) - parseDate(a.ef1))
        .slice(0, 2);

      const eventResponse = await client.graphql({
        query: listSlumSoccerEvents,
        variables: { limit: 10 },
      });

      const sortedEvents = eventResponse.data.listSlumSoccerEvents.items
        .filter((event) => event.date)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 2);

      setProjects(sortedProjects);
      setEvents(sortedEvents);
    } catch (error) {
      console.error("Error fetching projects and events:", error);
    }
  };

  return (
    <>
      {/* Hero section */}
      <header className="bg-[url('https://slumsoccer.s3.ap-south-1.amazonaws.com/images/bg4.jpg')] bg-cover bg-center h-[600px] flex items-center justify-center animate__animated animate__zoomIn">
        <div className="text-center bg-opacity-0 p-12 rounded-xl scroll-animate pt-48">
          <h2 className="text-5xl font-bold text-white mb-6">
            Empowering Children & Youth Through Sports
          </h2>
          <p className="text-xl text-white mb-8">
            Breaking barriers, creating opportunities and transforming lives !!
          </p>
        </div>
      </header>

      {/* Who We Are Section */}
      <section id="about" className="container mx-auto py-16 px-9">
        {/* Who We Are - Flex Container */}
        <div className="flex flex-col md:flex-row gap-12 items-center mb-16">
          <div className="w-full md:w-1/2 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
            <h3 className="text-4xl font-bold text-[#FF6600] mb-6">
              Who Are We – Slum Soccer
            </h3>
            <p className="text-lg text-gray-700 mb-6">
              Slum Soccer is a nonprofit organization that uses the power of
              sports to bring positive change in the lives of marginalized
              communities across India.
            </p>
            <p className="text-lg text-gray-700 mb-6">
              Founded in 2001 in Nagpur, Slum Soccer works with individuals
              facing challenges such as homelessness, poverty, lack of
              education, gender inequality, and limited access to opportunities.
            </p>
            <p className="text-lg text-gray-700 mb-4">
              At its core, Slum Soccer believes that sports are more than just
              games—they are a powerful tool for:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start"><span className="text-[#FF6600] mr-2">●</span> Building confidence and life skills</li>
              <li className="flex items-start"><span className="text-[#FF6600] mr-2">●</span> Promoting education and health awareness</li>
              <li className="flex items-start"><span className="text-[#FF6600] mr-2">●</span> Creating leadership opportunities</li>
              <li className="flex items-start"><span className="text-[#FF6600] mr-2">●</span> Empowering girls and women</li>
              <li className="flex items-start"><span className="text-[#FF6600] mr-2">●</span> Supporting social inclusion and community development</li>
            </ul>
          </div>
          <div className="w-full md:w-1/2 flex justify-center items-center scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-400">
            <img
              src="https://slum-soccer.s3.ap-south-1.amazonaws.com/uploads/WhoWeAre1.jpg"
              alt="Who We Are 1"
              className="rounded-xl shadow-lg w-full h-auto"
            />
          </div>
        </div>

        {/* Our Programs - Flex Container */}
        <div className="flex flex-col md:flex-row gap-12 items-center mb-10">
          <div className="w-full md:w-1/2 flex justify-center items-center order-2 md:order-1 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-400">
            <img
              src="https://slum-soccer.s3.ap-south-1.amazonaws.com/uploads/WhoWeAre2.jpg"
              alt="Who We Are 2"
              className="rounded-xl shadow-lg w-full h-auto"
            />
          </div>
          
          <div className="w-full md:w-1/2 order-1 md:order-2 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
            <h3 className="text-3xl font-bold text-[#FF6600] mb-6">
              Our Programs include:
            </h3>
            <ul className="space-y-4 text-gray-700">
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Deafgoal Fellowship</strong> – empowering deaf youth through sports, leadership, and inclusion</li>
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Community Impact</strong> – engaging local communities through sports-based development initiatives</li>
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Edukick</strong> – integrating education with sports to improve learning outcomes</li>
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Shakti Girls</strong> – empowering adolescent girls through sport, life skills, and leadership</li>
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Sports for Good City</strong> – building safer, inclusive cities through sports interventions</li>
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Play for Progress</strong> – using play to promote social change and youth development</li>
              <li><span className="text-[#FF6600] mr-2">●</span><strong>Pitch for Progress</strong> – creating access to safe playing spaces and opportunities</li>
            </ul>
            <p className="mt-8 text-lg text-gray-700">
              The organization also provides national and international sports
              pathways, including participation in the Homeless World Cup as
              India partner.
            </p>
            <p className="mt-4 text-lg text-gray-700">
              Over the years, Slum Soccer has impacted thousands of lives by
              creating safe spaces where individuals can learn, grow, and
              realize their potential.
            </p>
          </div>
        </div>

        {/* Mission Statement */}
        <div className="bg-[#fdf3e7] p-8 rounded-xl border-l-4 border-[#FF6600] scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-500 mt-12">
          <h4 className="text-3xl font-bold text-[#FF6600] mb-4">Our Mission</h4>
          <p className="text-lg text-gray-700">
            Uplift and transform underserved children and youth through the transformative power of sports, helping them overcome adversity and build a brighter future.
          </p>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="bg-[#efc94c] text-white py-16">
        <div className="container mx-auto text-center">
          <h3 className="text-4xl font-bold mb-12 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
            Our Impact in 2025
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
              <h4 className="text-5xl font-bold mb-4">15,215</h4>
              <p className="text-lg">Players Reached</p>
            </div>
            <div className="p-8 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
              <h4 className="text-5xl font-bold mb-4">520</h4>
              <p className="text-lg">Fellows And Leaders</p>
            </div>
            <div className="p-8 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
              <h4 className="text-5xl font-bold mb-4">10</h4>
              <p className="text-lg">States</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="container mx-auto py-16 px-9">
        <h3 className="text-4xl font-bold text-[#FF6600] text-center mb-12 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
          Our Projects
        </h3>
        <div className="grid md:grid-cols-2 gap-12">
          {projects.map((project) => (
            <ProjectCard key={project.projectid} project={project} />
          ))}
        </div>
        <div className="text-center mt-12 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
          <Link
            to="/projects"
            className="bg-[#FF6600] text-white px-8 py-3 rounded-full text-lg hover:bg-[#E65100] transition"
          >
            View All Projects
          </Link>
        </div>
      </section>

      {/* Upcoming Events Section */}
      <section id="events" className="container mx-auto py-16 px-9">
        <h3 className="text-4xl font-bold text-[#FF6600] text-center mb-12 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
          Up-Coming Event
        </h3>
        <div className="grid md:grid-cols-2 gap-12">
          {events.map((event) => (
            <EventCard key={event.eventId} event={event} />
          ))}
        </div>
        <div className="flex justify-center mt-12 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
          <Link
            to="/events"
            className="bg-[#FF6600] text-white px-8 py-4 rounded-full text-lg hover:bg-[#E65100] transition"
          >
            View All Up-Coming Event
          </Link>
        </div>
      </section>

      {/* Donation Section */}
      <section id="donate" className="bg-gray-100 py-14">
        <div className="container mx-auto text-center">
          <h3 className="text-4xl font-bold text-[#FF6600] mb-8 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
            Support Our Mission
          </h3>
          <p className="text-xl text-gray-700 mb-12 scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
            Your contribution can transform the lives of deaf youth around the world
          </p>
          <div className="flex justify-center scroll-animate opacity-0 translate-y-50 transition-all duration-1000 delay-200">
            <a
              href="https://payu.in/web/96B35888A53400A06AA7EBE160A0351C"
              className="border-2 border-[#f89816] text-[#f89816] px-8 py-4 rounded-full text-lg hover:bg-[#f89816] hover:text-white transition"
            >
              Donate Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
